import {skipToken} from '@reduxjs/toolkit/query';

import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetVehiclePhotosQuery, useListVehicleAlbumsQuery} from '@dms/api/sales';
import {VehicleAlbums} from '@dms/api/shared';
import {useListSaleVehiclePhotosByVehicleQuery} from '@dms/api/vehicle';

import {Nullish} from 'shared/web';

import {isAlbumVideo} from '../utils/getGridPhotos';
import {useVehiclePhotosPermissions} from './useVehiclePhotosPermissions';

export function useVehicleCoverPhoto(vehicleId: string | Nullish) {
  const {canReadVehicleAlbumPhotos} = useVehiclePhotosPermissions({
    vehicleId: vehicleId ?? '',
  });

  const isReadable = canReadVehicleAlbumPhotos && isNotNil(vehicleId);

  const {data: saleVehiclePhotos} = useListSaleVehiclePhotosByVehicleQuery(
    {
      vehicleId: vehicleId ?? '',
    },
    {
      skip: !isReadable,
    }
  );

  const hasSalePhotos = isNotNilOrEmpty(saleVehiclePhotos?.photos);

  // The vehicle-centric endpoint is the only source of service-case photos. Service / rental
  // vehicles use it as their primary gallery, while sale vehicles need it to surface the
  // service-case photos that the sale-vehicle gallery does not include.
  const {data: vehiclePhotos} = useGetVehiclePhotosQuery(
    {vehicleId: vehicleId ?? ''},
    {skip: !isReadable}
  );

  // Album metadata is only needed to pick out the service-case photos to graft onto the sale
  // gallery; service / rental vehicles already expose every album photo, so it stays skipped.
  const {data: vehicleAlbums} = useListVehicleAlbumsQuery(
    isReadable && hasSalePhotos ? {vehicleId: vehicleId ?? ''} : skipToken
  );

  const serviceCaseAlbumIds = new Set(
    (vehicleAlbums ?? [])
      .filter(
        (album) => album.name === VehicleAlbums.PHOTOS_SERVICE_CASE || isNotNil(album.serviceCaseId)
      )
      .map((album) => album.id)
  );

  const salePhotos = saleVehiclePhotos?.photos ?? [];

  // Videos stay out of the header carousel (T20-132145); the BE keeps them in the vehicle-photo
  // list on purpose. `unknown` media is kept — legacy files predate the BE fileType column and
  // are almost always images. Sale-vehicle photos never carry `mediaType` and are unaffected.
  const albumPhotos = (vehiclePhotos ?? []).filter((photo) => !isAlbumVideo(photo));

  // Service-case photos live only in the vehicle-centric endpoint and are appended to the sale
  // gallery (skipping any already present by fileId so they are not shown twice).
  const serviceCasePhotos = albumPhotos.filter(
    (photo) =>
      serviceCaseAlbumIds.has(photo.albumId) &&
      !salePhotos.some((salePhoto) => salePhoto.fileId === photo.fileId)
  );

  // Sale vehicles keep their gallery as the base and append the otherwise-hidden service-case
  // photos; service / rental vehicles return every vehicle photo as before.
  const photos = hasSalePhotos ? [...salePhotos, ...serviceCasePhotos] : albumPhotos;

  const coverPhoto = photos.find((photo) => photo.isCoverPhoto);

  return {
    photos,
    coverPhoto,
  };
}
